import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { getAuthToken, useAuthStore } from '@/stores/authStore'
import toast from 'react-hot-toast'
import { API_CONFIG } from '@/config/api'
import { customerPostLogoutPath } from '@/lib/customerShopReturn'
import { markStaffLogoutIntent } from '@/lib/staffLoginRedirect'

export type ApiRequestConfig = AxiosRequestConfig & { suppressErrorToast?: boolean }

/** Axios (AbortController) + navigation/reload — not a real network failure; never toast these. */
function isAbortedOrCanceledRequest(error: unknown): boolean {
  if (error == null || typeof error !== 'object') return false
  const e = error as { code?: string; name?: string; message?: string }
  if (e.code === 'ERR_CANCELED') return true
  if (e.name === 'CanceledError' || e.name === 'AbortError') return true
  if (typeof e.message === 'string' && /^cancell?ed$/i.test(e.message.trim())) return true
  return false
}

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
    // Avoid stale GET responses (e.g. page flags) when proxies or browsers cache API calls
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  },
})

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Try to get token from store first, then fallback to localStorage
    let token = getAuthToken()
    if (!token) {
      token = localStorage.getItem('auth-token')
    }
    
    // Log token status for customer orders endpoint
    if (config.url?.includes('/orders/my') || config.url?.includes('/orders/customer')) {
      console.log('API Request - Customer Orders:', {
        url: config.url,
        token: token ? 'Present' : 'Missing',
        tokenLength: token?.length,
        authState: useAuthStore.getState()
      })
    }
    
    // Log request data for subscription creation
    if (config.url?.includes('/subscriptions') && config.method === 'post' && config.data) {
      console.log('=== AXIOS REQUEST INTERCEPTOR - SUBSCRIPTION DATA ===');
      console.log('Request data type:', typeof config.data);
      console.log('Has custom_entries:', 'custom_entries' in (config.data || {}));
      console.log('Has discount_items:', 'discount_items' in (config.data || {}));
      console.log('custom_entries value:', config.data.custom_entries);
      console.log('custom_entries is array:', Array.isArray(config.data.custom_entries));
      console.log('custom_entries length:', config.data.custom_entries?.length || 0);
      console.log('discount_items value:', config.data.discount_items);
      console.log('discount_items is array:', Array.isArray(config.data.discount_items));
      console.log('discount_items length:', config.data.discount_items?.length || 0);
      console.log('Full request data (stringified, first 3000 chars):', JSON.stringify(config.data, null, 2).substring(0, 3000));
    }
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    } else {
      console.warn('API Request - No token available for:', config.url)
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete (config.headers as Record<string, unknown>)['Content-Type']
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Single-flight refresh so parallel 401s + session timer share one /auth/refresh.
let refreshInFlight: Promise<string | null> | null = null

/**
 * Rotate access token via HttpOnly refresh cookie. Safe to call from many places at once
 * in the same tab. Cross-tab races are handled server-side (atomic claim + grace).
 */
export async function refreshAccessTokenOnce(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = api
    .post('/auth/refresh', undefined, { suppressErrorToast: true } as ApiRequestConfig)
    .then((res) => {
      const payload = res.data as {
        success?: boolean
        data?: Parameters<ReturnType<typeof useAuthStore.getState>['applySession']>[0]
      }
      if (payload?.success && payload.data?.token) {
        useAuthStore.getState().applySession(payload.data)
        return payload.data.token
      }
      return null
    })
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null
    })

  return refreshInFlight
}

function forceLogoutToLogin(serverMessage?: string, code?: string) {
  const currentPath = window.location.pathname
  const onSignupFlow = currentPath.startsWith('/signup')
  let message =
    typeof serverMessage === 'string' && serverMessage.trim()
      ? serverMessage.trim()
      : 'Session expired. Please login again.'
  if (code === 'session_idle' || /due to inactivity/i.test(message)) {
    message = 'Signed out due to inactivity.'
  } else if (code === 'session_expired' && /session expired/i.test(message)) {
    message = 'Session expired. Please sign in again.'
  } else if (/refresh token is required/i.test(message)) {
    message = 'Your sign-in cookie is missing. Please sign in again.'
  } else if (/invalid refresh token/i.test(message)) {
    message = 'Your session could not be renewed. Please sign in again.'
  }
  // Signup / verify are public; a stale staff JWT must not toast+redirect away mid-flow.
  if (!currentPath.includes('/auth/') && !onSignupFlow) {
    toast.error(message)
  }
  const authState = useAuthStore.getState()
  const isCustomer = authState.user?.is_web_user
  if (!isCustomer) {
    markStaffLogoutIntent()
  }
  // Logout 401 no longer re-enters forceLogout, so server revoke is safe here.
  void authState.performLogout()
  if (!currentPath.includes('/auth/') && !onSignupFlow) {
    setTimeout(() => {
      window.location.href = isCustomer ? customerPostLogoutPath() : '/auth/staff/login'
    }, 2000)
  }
}

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  async (error) => {
    if (isAbortedOrCanceledRequest(error)) {
      return Promise.reject(error)
    }

    if (error.response) {
      const { status, data } = error.response
      const requestUrl = error.config?.url || ''
      const suppressErrorToast = (error.config as ApiRequestConfig | undefined)?.suppressErrorToast
      const isShopStorefront = requestUrl.includes('/shop-website/')

      // Public storefront pages show their own checkout/cart messaging.
      if (isShopStorefront) {
        return Promise.reject(error)
      }

      // Soft-fail platform support when disabled/unconfigured (widget shows its own state).
      // suppressErrorToast still allows 401 handling below.
      const isPlatformSupportSoftFail =
        requestUrl.includes('/support/platform/') && (status === 403 || status === 503)
      if ((suppressErrorToast || isPlatformSupportSoftFail) && status !== 401) {
        return Promise.reject(error)
      }

      // Handle different error status codes
      switch (status) {
        case 401: {
          console.error('401 Error:', {
            url: error.config?.url,
            method: error.config?.method,
            data: data,
            authState: useAuthStore.getState(),
            token: getAuthToken() ? 'Present' : 'Missing',
            localStorageToken: localStorage.getItem('auth-token') ? 'Present' : 'Missing',
          })

          if (data?.message === 'Access token is required') {
            break
          }

          const originalRequest = error.config as ApiRequestConfig & { _retry?: boolean }
          const code = typeof data?.code === 'string' ? data.code : ''
          const isHardSessionEnd =
            code === 'session_idle' || code === 'session_expired' || code === 'mfa_required'
          const isRefreshCall = requestUrl.includes('/auth/refresh')
          const isLogoutCall = requestUrl.includes('/auth/logout')
          const isAuthEndpoint =
            isRefreshCall ||
            isLogoutCall ||
            requestUrl.includes('/auth/login') ||
            requestUrl.includes('/auth/staff/login') ||
            requestUrl.includes('/auth/customer/login')

          // Refresh helper and intentional logout must not cascade into another force-logout toast.
          if (isRefreshCall || isLogoutCall) {
            break
          }

          if (!isHardSessionEnd && !isAuthEndpoint && originalRequest && !originalRequest._retry) {
            originalRequest._retry = true
            const newToken = await refreshAccessTokenOnce()
            if (newToken) {
              originalRequest.headers = originalRequest.headers || {}
              ;(originalRequest.headers as Record<string, string>).Authorization =
                `Bearer ${newToken}`
              return api.request(originalRequest)
            }
          }

          forceLogoutToLogin(
            typeof data?.message === 'string' && data.message.trim() ? data.message : undefined,
            code || undefined,
          )
          break
        }

        case 403: {
          const method = (error.config?.method || 'get').toLowerCase()
          const message = typeof data?.message === 'string' ? data.message : ''
          const code = typeof data?.code === 'string' ? data.code : ''
          if (code === 'shop_inactive' || code === 'staff_login_disabled') {
            forceLogoutToLogin(message.trim() || 'This shop is unavailable.')
            break
          }
          const isPermissionDenied =
            message.includes('Insufficient permissions') ||
            message.includes('Manager or admin access required') ||
            message.includes('Admin access required')
          // Route guards show No Permission; avoid noisy toasts on read-only page loads.
          if (method === 'get' && (isPermissionDenied || code === 'PLAN_FEATURE_REQUIRED')) {
            break
          }
          toast.error(message.trim() || 'Access denied. You don\'t have permission to perform this action.')
          break
        }

        case 400:
          // Validation / bad request errors
          if (data.errors && Array.isArray(data.errors)) {
            data.errors.forEach((err: string) => toast.error(err))
          } else {
            toast.error(data.message || 'Invalid request.')
          }
          break

        case 404:
          toast.error('Resource not found.')
          break

        case 409:
          toast.error(data.message || 'Resource already exists.')
          break

        case 422:
          // Validation errors
          if (data.errors && Array.isArray(data.errors)) {
            data.errors.forEach((error: string) => toast.error(error))
          } else {
            toast.error(data.message || 'Validation error.')
          }
          break

        case 429:
          toast.error('Too many requests. Please try again later.')
          break

        case 500:
          toast.error(
            typeof data?.message === 'string' && data.message.trim()
              ? data.message
              : 'Server error. Please try again later.'
          )
          break

        default:
          toast.error(data.message || 'An unexpected error occurred.')
      }
    } else if (error.request) {
      const requestUrl = error.config?.url || ''
      const suppressErrorToast = (error.config as ApiRequestConfig | undefined)?.suppressErrorToast
      if (requestUrl.includes('/shop-website/') || suppressErrorToast) {
        return Promise.reject(error)
      }
      // No HTTP response: wrong URL, server down, CORS block (browser often hides the real error), or timeout.
      const err = error as { message?: string; code?: string; config?: { baseURL?: string; url?: string } }
      if (import.meta.env.DEV) {
        console.error('[API] No response from server. Common causes: API not on this URL, CORS, or use 127.0.0.1 vs localhost.', {
          message: err.message,
          code: err.code,
          baseURL: err.config?.baseURL,
          url: err.config?.url,
        })
      }
      if (err.code === 'ECONNABORTED') {
        toast.error(
          'Request timed out. The connection may be slow — wait and try again, or check that the OMS server is reachable.'
        )
      } else {
        toast.error(
          import.meta.env.DEV
            ? "Can't reach the API. In dev, confirm the backend is on port 3001, use the Vite app at http://localhost:3000 (proxy) or set VITE_API_URL / CORS_ORIGIN. See console for details."
            : 'Network error. Please check your connection.'
        )
      }
    } else {
      // Other error
      toast.error('An unexpected error occurred.')
    }

    return Promise.reject(error)
  }
)

// Generic API methods
export const apiClient = {
  get: <T = any>(url: string, config?: ApiRequestConfig): Promise<T> =>
    api.get(url, config).then(response => response.data),

  post: <T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<T> =>
    api.post(url, data, config).then(response => response.data),

  put: <T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<T> =>
    api.put(url, data, config).then(response => response.data),

  patch: <T = any>(url: string, data?: any, config?: ApiRequestConfig): Promise<T> =>
    api.patch(url, data, config).then(response => response.data),

  delete: <T = any>(url: string, config?: ApiRequestConfig): Promise<T> =>
    api.delete(url, config).then(response => response.data),
}

// File upload helper
export const uploadFile = async (
  url: string,
  file: File,
  additionalData?: Record<string, any>,
  onProgress?: (progress: number) => void
) => {
  const formData = new FormData()
  formData.append('file', file)

  if (additionalData) {
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key])
    })
  }

  return api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        onProgress(progress)
      }
    },
  }).then(response => response.data)
}

// Download helper
export const downloadFile = async (url: string, filename?: string) => {
  try {
    const response = await api.get(url, {
      responseType: 'blob',
    })

    const blob = new Blob([response.data])
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    link.download = filename || 'download'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(downloadUrl)
  } catch (error) {
    toast.error('Failed to download file')
    throw error
  }
}

// Query string builder
export const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams()
  
  Object.keys(params).forEach(key => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(item => searchParams.append(key, item))
      } else {
        searchParams.append(key, value.toString())
      }
    }
  })
  
  return searchParams.toString()
}

export default api
